// @ts-nocheck
import _ from 'lodash';
import axios from 'axios';
import { create } from 'zustand';
import { API_URL } from '../../configs';
import { toastTypes } from '../../enums';
import getRoot from '../root';

const DEFAULT_CONFIGS = { audienceIdx: undefined, enabled: false, scope: 'https://www.googleapis.com/auth/adwords' };

const useGoogleAdwordsStore = create((set, get) => ({
  configs: DEFAULT_CONFIGS,
  audiences: [],
  inputConfigs: DEFAULT_CONFIGS,
  loading: { getAudiences: false, getAuthUrl: false, getConfigs: false, updateConfigs: false, setup: false },
  authUrl: '',

  anyLoading: () => _.includes(get().loading, true),

  setConfigField: (name, value) => set((s) => ({ configs: { ...s.configs, [name]: value } })),

  reset: () => set({
    configs: { userListId: '', userListType: '', enabled: false },
    audiences: [],
    inputConfigs: { userListId: '', userListType: '', enabled: false },
    loading: _.mapValues(get().loading, () => false),
  }),

  handleInput: (_e, el) => {
    const value = el.type === 'checkbox' ? el.checked : el.value;
    set((s) => ({ inputConfigs: { ...s.inputConfigs, [el.name]: value } }));
  },

  resetInputConfigs: () => set((s) => ({ inputConfigs: s.configs })),

  getAuthUrl: async () => {
    try {
      const res = await axios.get(`${API_URL}/api/integrations/GoogleAdwords/auth-url`);
      if (res.data.success) {
        set({ authUrl: res.data.authUrl });
        return res.data.authUrl;
      }
    } catch (e) { /* ignore */ }
    return null;
  },

  openAuthPage: async () => {
    try {
      const res = await axios.get(`${API_URL}/api/integrations/GoogleAdwords/auth-url`);
      if (res.data.success) window.open(res.data.authUrl, '_blank');
    } catch (e) { /* ignore */ }
  },

  getConfigs: async () => {
    try {
      set((s) => ({ loading: { ...s.loading, getConfigs: true } }));
      const { data } = await axios.get(`${API_URL}/api/integrations/GoogleAdwords/config`);
      if (data.success) {
        const configs = { ...get().configs, ...data.config };
        set({ configs, inputConfigs: configs });
      }
    } catch (e) { /* ignore */ } finally {
      set((s) => ({ loading: { ...s.loading, getConfigs: false } }));
    }
  },

  getAudiences: async () => {
    const root = getRoot();
    try {
      set((s) => ({ loading: { ...s.loading, getAudiences: true } }));
      const { data } = await axios.get(`${API_URL}/api/integrations/GoogleAdwords/audiences`);
      if (data.success) {
        const next = { audiences: data.audiences };
        const { configs } = get();
        if (configs.userListId) {
          next.inputConfigs = {
            ...get().inputConfigs,
            audienceIdx: data.audiences.findIndex((a) => a.id === configs.userListId),
          };
        }
        set(next);
      }
    } catch (e) {
      root.failure('Could not get audiences!');
    } finally {
      set((s) => ({ loading: { ...s.loading, getAudiences: false } }));
    }
  },

  updateConfigs: async () => {
    const root = getRoot();
    try {
      set((s) => ({ loading: { ...s.loading, updateConfigs: true } }));
      const { inputConfigs, audiences } = get();
      const { data } = await axios.post(`${API_URL}/api/integrations/GoogleAdwords/config`, {
        ...inputConfigs,
        userListId: audiences[inputConfigs.audienceIdx].id,
      });
      if (data.success) {
        root.success('Successfully updated Adwords configurations', toastTypes.updateConfigs);
        root.clearToast(toastTypes.updateConfigs);
      } else throw new Error();
    } catch (e) {
      root.failure('Something went wrong...', toastTypes.updateConfigs);
    } finally {
      get().getConfigs();
      set((s) => ({ loading: { ...s.loading, updateConfigs: false } }));
    }
  },

  setup: async (code, scope) => {
    const root = getRoot();
    try {
      set((s) => ({ loading: { ...s.loading, setup: true } }));
      const { data } = await axios.post(`${API_URL}/api/integrations/GoogleAdwords/setup`, { code, scope });
      if (data.success) {
        root.success('GoogleAdwords integration successful.', toastTypes.setup);
        root.clearToast(toastTypes.setup);
      } else throw new Error();
    } catch (e) {
      root.failure('GoogleAdwords integration unsuccessful... Try revoking Capture permissions at https://myaccount.google.com/permissions', toastTypes.setup);
    } finally {
      set((s) => ({ loading: { ...s.loading, setup: false } }));
      get().getConfigs();
    }
  },
}));

export default useGoogleAdwordsStore;
